"use client";

import { Link } from "@/i18n/routing";
import { cn, getFilePath } from "@/lib/utils/utils";
import Image from "next/image";
import HeaderLink from "../header/headerLink";
import { FaPhone, FaWhatsapp } from "react-icons/fa";
import SectionTitle from "@/components/shared/sectionTitle";
import { ChevronLeft, ChevronRight, Mail } from "lucide-react";
import useLocalizer from "@/hooks/useLocalizer";
import HeaderItem from "../header/headerItem";
import { useInfo } from "@/components/shared/infoProvider";
import Text from "@/components/shared/text";
import DynamicIcon from "@/components/shared/dynamicIcon";
import Flex from "@/components/core/flex";
import { useNavigation } from "@/components/shared/navigationProvider";

export default function Footer() {
  const { t, isRtl } = useLocalizer();
  const { data: clientInfo } = useInfo();
  const navigations = useNavigation();
  return (
    <footer className="relative overflow-hidden">
      <div
        className={cn("relative min-h-[500px] w-full flex items-center")}
        style={{
          backgroundImage: `url('/images/backgrounds/footer.jpg')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="w-full h-full absolute top-0 left-0 bottom-0 bg-secondary/95" />
        <div className="relative w-full h-full flex items-center z-20">
          <div className="container mx-auto p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ">
              <Flex direction="col" spacing={{ all: 8 }}>
                {clientInfo?.info?.lightLogo?.key && (
                  <Link href="/">
                    <Image
                      src={getFilePath(clientInfo?.info?.lightLogo?.key) ?? ""}
                      alt={
                        (isRtl
                          ? clientInfo?.info?.arBrandName
                          : clientInfo?.info?.enBrandName) ?? "logo"
                      }
                      priority
                      placeholder="empty"
                      height={52}
                      width={144}
                    />
                  </Link>
                )}
                <Text className="text-white text-balance">
                  {isRtl
                    ? (clientInfo?.info?.arDescription ?? "")
                    : (clientInfo?.info?.enDescription ?? "")}
                </Text>
                <Flex direction="row" flexWrap="wrap" spacing={{ all: 4 }}>
                  {clientInfo?.socialMedia?.map((link, index) => (
                    <HeaderLink
                      key={index}
                      title={
                        (isRtl ? link.arLinkTitle : link.enLinkTitle) ||
                        (isRtl ? "رابط التواصل الاجتماعي" : "Social Media Link")
                      }
                      href={link.linkHref ?? "#"}
                      icon={
                        <DynamicIcon
                          name={link.icon?.code ?? ""}
                          className={cn("text-white ")}
                          size={24}
                        />
                      }
                    />
                  ))}
                </Flex>
              </Flex>
              <Flex direction="col" spacing={{ all: 8 }}>
                <SectionTitle
                  title={t("footer.discover")}
                  titleTag="h3"
                  lineStyle="2"
                  titleClassName="text-white"
                />
                <ul className="flex flex-col gap-4 [&_li]:text-muted/80 [&_li]:hover:text-white">
                  {navigations?.navigations?.map((nav) => (
                    <li key={nav.id}>
                      <Link
                        href={nav.href ?? "#"}
                        className="flex flex-row gap-2 items-center"
                      >
                        {isRtl ? (
                          <ChevronLeft size={18} />
                        ) : (
                          <ChevronRight size={18} />
                        )}
                        <Text>{isRtl ? nav.arTitle : nav.enTitle}</Text>
                      </Link>
                    </li>
                  ))}
                </ul>
              </Flex>
              <Flex direction="col" spacing={{ all: 8 }}>
                <SectionTitle
                  title={t("footer.covers")}
                  titleTag="h3"
                  lineStyle="2"
                  titleClassName="text-white"
                />
                <ul className="flex flex-col gap-4 [&_li]:text-muted/80">
                  {clientInfo?.covers?.map((area, index) => (
                    <li key={index}>
                      <Flex items="center" direction="row" spacing={{ all: 2 }}>
                        {isRtl ? (
                          <ChevronLeft size={18} />
                        ) : (
                          <ChevronRight size={18} />
                        )}
                        <Text>{isRtl ? area.arName : area.enName}</Text>
                      </Flex>
                    </li>
                  ))}
                </ul>
              </Flex>

              <Flex direction="col" spacing={{ all: 8 }}>
                <SectionTitle
                  title={t("footer.contact")}
                  titleTag="h3"
                  lineStyle="2"
                  titleClassName="text-white"
                />
                <ul className="flex flex-col gap-4 [&_li]:text-muted/80 [&_li]:hover:text-white">
                  {clientInfo?.info?.mobileNumber && (
                    <li>
                      <HeaderItem
                        icon={FaPhone}
                        title={t("titles.callRightNow")}
                        titleClassName="text-muted"
                        item={
                          <Link
                            href={`tel:${clientInfo?.info?.mobileNumber}`}
                            className="text-white hover:text-muted/80"
                            aria-label={`${t("titles.callRightNow")}: ${clientInfo?.info?.mobileNumber}`}
                          >
                            {clientInfo?.info?.mobileNumber}
                          </Link>
                        }
                        itemClassName="hover:text-primary"
                        iconClassName="rounded-full bg-primary w-10 h-10 flex items-center justify-center text-white hover:bg-secondary transition-all duration-300 ease-in-out"
                      />
                    </li>
                  )}
                  {clientInfo?.info?.whatsAppMobileNumber && (
                    <li>
                      <HeaderItem
                        icon={FaWhatsapp}
                        title={t("titles.whatsapp")}
                        titleClassName="text-muted"
                        item={
                          <Link
                            href={`https://wa.me/${clientInfo?.info?.whatsAppMobileNumber}`}
                            className="text-white hover:text-muted/80"
                            aria-label={`${t("titles.whatsapp")}: ${clientInfo?.info?.whatsAppMobileNumber}`}
                          >
                            {clientInfo?.info?.whatsAppMobileNumber}
                          </Link>
                        }
                        itemClassName="hover:text-primary"
                        iconClassName="rounded-full bg-primary w-10 h-10 flex items-center justify-center text-white hover:bg-secondary transition-all duration-300 ease-in-out"
                      />
                    </li>
                  )}
                  {clientInfo?.info?.email && (
                    <li>
                      <HeaderItem
                        icon={Mail}
                        title={t("titles.email")}
                        titleClassName="text-muted"
                        item={
                          <Link
                            href={`mailto:${clientInfo?.info?.email}`}
                            className="text-white hover:text-muted/80"
                          >
                            {clientInfo?.info?.email}
                          </Link>
                        }
                        itemClassName="hover:text-primary"
                        iconClassName="rounded-full bg-primary w-10 h-10 flex items-center justify-center text-white hover:bg-secondary transition-all duration-300 ease-in-out"
                      />
                    </li>
                  )}
                </ul>
              </Flex>
            </div>
          </div>
        </div>
      </div>
      <div className="min-h-20 w-full bg-secondary flex items-center justify-center">
        <div className="container mx-auto">
          <Text className="text-white text-center">
            {t("footer.copyright", {
              name: "FutureSoft",
            })}
            <Text className="mx-2 font-bold" bdi as="span">
              {new Date().getFullYear()} ©
            </Text>
          </Text>
        </div>
      </div>
    </footer>
  );
}
